/**
 * A password field that can be revealed.
 *
 * Typing a password you cannot see is how people mistype one, and the two places it matters most
 * here are the ones with no second chance: an employee signing in on a phone keyboard, and an
 * administrator choosing a new password behind a confirmation field. Both had a bare
 * `type="password"`.
 *
 * ## Why this is a component and not a prop on `Input`
 *
 * The toggle changes `type`, which means the field is no longer a password field while it is
 * revealed — so the browser's own "show password" affordance, its autofill heuristics, and the
 * `autoComplete` hint all have to keep working across that switch. Keeping it in one component
 * means that reasoning exists once. `Input` stays a text field with a label.
 *
 * ## Details that are easy to get wrong
 *
 * * The button is `type="button"`. Inside a form, a button without one submits it — so the first
 *   press of "show" would have posted a half-typed login.
 * * It is `aria-pressed`, not a label that flips between "Show" and "Hide": a toggle announces its
 *   state, and a screen-reader user who tabs onto it should hear whether the password is currently
 *   visible rather than what the next press will do.
 * * The field keeps `autoComplete` while revealed. A password manager that stops recognising the
 *   field the moment someone reveals it is worse than no toggle.
 * * Revealing never outlives the page: there is no persistence, and the field goes back to hidden
 *   on remount. A shared branch terminal should not remember that the last person chose to show.
 * * The control sits inside the input's box and is excluded from the accessible name of the field,
 *   so the label still reads as the label.
 */

import { forwardRef, useId, useState, type InputHTMLAttributes } from "react";

import { cx } from "./cx";

export interface PasswordInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  label: string;
  error?: string;
  hint?: string;
  /** Accessible name for the toggle, e.g. "Show password". */
  showLabel: string;
  /** Accessible name once revealed, e.g. "Hide password". */
  hideLabel: string;
}

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput(
    { label, error, hint, showLabel, hideLabel, className, id, ...rest },
    ref,
  ) {
    const generated = useId();
    const inputId = id ?? generated;
    const hintId = hint ? `${inputId}-hint` : undefined;
    const errorId = error ? `${inputId}-error` : undefined;
    const [revealed, setRevealed] = useState(false);

    return (
      <div className="flex flex-col gap-1">
        <label htmlFor={inputId} className="text-label-md text-textPrimary">
          {label}
        </label>
        <div className="relative flex items-center">
          <input
            ref={ref}
            id={inputId}
            type={revealed ? "text" : "password"}
            aria-invalid={error ? true : undefined}
            aria-describedby={cx(hintId, errorId) || undefined}
            // A password is never the right direction to infer from content: it is not prose, and
            // an Arabic interface must not right-align a Latin password as it is typed.
            dir="ltr"
            className={cx(
              "w-full rounded-sm border bg-surfaceRaised px-3 py-2 text-body-md text-textPrimary",
              // Room for the toggle, on whichever side the interface ends.
              "pe-11",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
              error ? "border-statusDanger" : "border-borderStrong",
              className,
            )}
            {...rest}
          />
          <button
            type="button"
            onClick={() => setRevealed((previous) => !previous)}
            aria-pressed={revealed}
            aria-label={revealed ? hideLabel : showLabel}
            aria-controls={inputId}
            // `end-1` rather than `right-1`: in Arabic the field ends on the left.
            className={cx(
              "absolute end-1 flex h-8 w-8 items-center justify-center rounded-sm",
              "text-textMuted hover:text-textPrimary",
              "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary",
            )}
          >
            <EyeIcon crossed={revealed} />
          </button>
        </div>
        {hint ? (
          <span id={hintId} className="text-label-md text-textMuted">
            {hint}
          </span>
        ) : null}
        {error ? (
          <span id={errorId} role="alert" className="text-label-md text-statusDanger">
            {error}
          </span>
        ) : null}
      </div>
    );
  },
);

/** `aria-hidden`: the button already carries the name, and announcing both reads it twice. */
function EyeIcon({ crossed }: { crossed: boolean }): JSX.Element {
  return (
    <svg
      aria-hidden="true"
      focusable="false"
      viewBox="0 0 20 20"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M1.7 10S4.9 4.6 10 4.6 18.3 10 18.3 10 15.1 15.4 10 15.4 1.7 10 1.7 10Z" />
      <circle cx="10" cy="10" r="2.6" />
      {crossed ? <path d="M3.4 3.4l13.2 13.2" /> : null}
    </svg>
  );
}
